import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { protocolDays } from '@/data/mockData';
import { motivationalMessages } from '@/data/mockData';

export interface BodySignal {
  value: string;
  trend: string;
  interpretation: string;
}

export interface ProtocolInfo {
  currentDay: number;
  dayTitle: string;
  daySubtitle: string;
  objective: string;
  estimatedMinutes: number;
  totalCompleted: number;
  consecutiveDays: number;
}

export interface WeeklyStats {
  checkinsCount: number;
  consecutiveProtocolDays: number;
  bestSignal: string;
}

export interface DashboardData {
  loading: boolean;
  userName: string;
  avatarUrl: string;
  mainGoal: string | null;
  motivationLevel: string | null;
  foodPreferences: string | null;
  restrictions: string | null;

  // Header
  adaptiveMessage: string;

  // Hero
  consistency: number;
  tasksDone: number;
  totalTasks: number;
  streak: number;
  comparisonText: string;

  // Check-in state
  checkinDone: boolean;
  protocolDoneToday: boolean;
  waterToday: number;

  // Body signals
  hydration: BodySignal;
  bowel: BodySignal;
  energy: BodySignal;
  bloating: BodySignal;

  // Protocol
  protocol: ProtocolInfo;

  // Weekly
  weekly: WeeklyStats;

  // Insight
  insightMessage: string;

  // Daily focus items
  focusItems: string[];

  // Daily summary
  dailySummary: string;
}

export const useDashboardData = (): DashboardData => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState('Usuario/a');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [mainGoal, setMainGoal] = useState<string | null>(null);
  const [motivationLevel, setMotivationLevel] = useState<string | null>(null);
  const [foodPreferences, setFoodPreferences] = useState<string | null>(null);
  const [restrictions, setRestrictions] = useState<string | null>(null);

  // Raw data
  const [todayCheckin, setTodayCheckin] = useState<any>(null);
  const [yesterdayCheckin, setYesterdayCheckin] = useState<any>(null);
  const [weekCheckins, setWeekCheckins] = useState<any[]>([]);
  const [protocolCompletions, setProtocolCompletions] = useState<any[]>([]);

  useEffect(() => {
    const fetchAll = async () => {
      console.log("DEBUG DASHBOARD: currentUser state", user);
      if (!user || !user.id) {
        console.log("DEBUG DASHBOARD: No user or user.id, skipping fetch.");
        return;
      }

      // Fetch all in one request via specialized dashboard proxy
      const res = await fetch('/api/dashboard/data');
      const data = await res.json();

      if (!res.ok) {
        console.error("Error fetching dashboard data:", data.error);
        setLoading(false);
        return;
      }

      const { profile, todayCheckin, yesterdayCheckin, weekCheckins, protocolCompletions } = data;

      // Profile
      if (profile) {
        if (profile.full_name) setUserName(profile.full_name);
        if (profile.avatar_url) setAvatarUrl(profile.avatar_url);
        setMainGoal(profile.main_goal);
        setMotivationLevel(profile.motivation_level);
        setFoodPreferences(profile.food_preferences);
        setRestrictions(profile.restrictions);
      } else {
        const localName = localStorage.getItem('user_name');
        if (localName) setUserName(localName);
      }

      // State updates
      setTodayCheckin(todayCheckin);
      setYesterdayCheckin(yesterdayCheckin);
      setWeekCheckins(weekCheckins);
      setProtocolCompletions(protocolCompletions);

      setLoading(false);
    };

    fetchAll();
  }, [user]);

  // Derived computations
  const derived = useMemo(() => {
    const tc = todayCheckin;
    const yc = yesterdayCheckin;

    // --- Tasks & Consistency ---
    let tasksDone = 0;
    if (tc) {
      if (tc.notes?.includes('[Protocolo: Completado]')) tasksDone++;
      if (tc.water_glasses && tc.water_glasses > 0) tasksDone++;
      if (tc.poop_quality && tc.poop_quality !== '-') tasksDone++;
    }
    const totalTasks = 3;
    const consistency = tasksDone > 0 ? Math.round((tasksDone / totalTasks) * 100) : 0;

    // --- Streak ---
    let streak = 0;
    let streakBroken = false;
    const today = new Date();
    
    // Check today and yesterday first to see if streak is alive
    const todayStr = today.toLocaleDateString('en-CA');
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toLocaleDateString('en-CA');
    
    const hasToday = weekCheckins.some((c: any) => c.checkin_date === todayStr);
    const hasYesterday = weekCheckins.some((c: any) => c.checkin_date === yesterdayStr);
    
    if (!hasToday && !hasYesterday) {
      streak = 0;
    } else {
      for (let i = 0; i < 30; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const dateStr = d.toLocaleDateString('en-CA');
        const found = weekCheckins.find((c: any) => c.checkin_date === dateStr);
        
        if (found) {
          streak++;
        } else if (i > 0) { // If we miss a day that isn't today, the streak breaks
          break;
        }
      }
    }

    // --- Yesterday comparison ---
    let yesterdayTasks = 0;
    if (yc) {
      if (yc.notes?.includes('[Protocolo: Completado]')) yesterdayTasks++;
      if (yc.water_glasses && yc.water_glasses > 0) yesterdayTasks++;
      if (yc.poop_quality && yc.poop_quality !== '-') yesterdayTasks++;
    }
    let comparisonText = '';
    if (yc) {
      if (tasksDone > yesterdayTasks) comparisonText = 'Mejor que ayer ↑';
      else if (tasksDone === yesterdayTasks) comparisonText = 'Igual que ayer →';
      else comparisonText = 'Ayer fue mejor ↓';
    }

    // --- Adaptive header message ---
    let adaptiveMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
    if (tc && tasksDone === totalTasks) {
      adaptiveMessage = '¡Gran día! Tus acciones clave están completas. 🌟';
    } else if (tc && tc.energy_level === 'Baja') {
      adaptiveMessage = 'Un paso a la vez. Empieza con algo simple hoy. 💛';
    } else if (!tc && streak === 0 && weekCheckins.length < 3) {
      adaptiveMessage = 'No pasa nada. Hoy es un buen día para volver. 🍃';
    } else if (streak >= 3) {
      adaptiveMessage = 'Tu consistencia está creando resultados reales. 💚';
    }

    // --- Body signals ---
    const waterVal = tc?.water_glasses || 0;
    const waterDiff = waterVal - (yc?.water_glasses || 0);
    const hydration: BodySignal = {
      value: `${waterVal} vasos`,
      trend: yc ? (waterDiff > 0 ? `+${waterDiff} vs ayer` : waterDiff < 0 ? `${waterDiff} vs ayer` : 'Igual que ayer') : '',
      interpretation: waterVal >= 6 ? 'Excelente hidratación' : waterVal >= 4 ? 'Buen nivel' : waterVal > 0 ? 'Intenta beber más agua' : 'Registra tu agua hoy',
    };

    const bowelVal = tc?.poop_quality || '-';
    const bowel: BodySignal = {
      value: bowelVal,
      trend: '',
      interpretation: bowelVal === 'Fui normalmente' ? 'Tu digestión fluye bien' :
        bowelVal === 'Fui más de una vez' ? 'Muy activo hoy' :
        bowelVal === 'Fui con dificultad' ? 'Prioriza agua + fibra' :
        bowelVal === 'No fui al baño' ? 'Aumenta fibra y movimiento' : '',
    };

    const energyVal = tc?.energy_level || '-';
    const energy: BodySignal = {
      value: energyVal,
      trend: '',
      interpretation: energyVal === 'Excelente' ? 'Energía al máximo' :
        energyVal === 'Buena' ? 'Estable hoy' :
        energyVal === 'Moderada' ? 'Nivel aceptable' :
        energyVal === 'Baja' ? 'Necesitas más descanso' : '',
    };

    const bloatingVal = tc?.bloating_level || '-';
    const bloating: BodySignal = {
      value: bloatingVal,
      trend: '',
      interpretation: bloatingVal === 'Ninguna' ? 'Tu mejor señal hoy' :
        bloatingVal === 'Leve' ? 'Bajo control' :
        bloatingVal === 'Moderada' ? 'Opta por comidas ligeras' :
        bloatingVal === 'Alta' ? 'Priorizar comidas ligeras' : '',
    };

    // --- Check-in state ---
    const checkinDone = !!tc;
    const protocolDoneToday = tc?.notes?.includes('[Protocolo: Completado]') || false;

    // --- Protocol info ---
    const totalCompleted = protocolCompletions.length;
    const currentDayIdx = Math.min(totalCompleted, protocolDays.length - 1);
    const currentProtocol = protocolDays[currentDayIdx];

    // Consecutive protocol days
    let consecutiveProtocolDays = 0;
    const sortedCompletions = [...protocolCompletions].sort((a: any, b: any) =>
      new Date(b.completed_at || b.created_at).getTime() - new Date(a.completed_at || a.created_at).getTime()
    );
    for (let i = 0; i < sortedCompletions.length; i++) {
      const dayNum = sortedCompletions[i].day_number;
      if (dayNum === currentDayIdx - i - 1 || i === 0) {
        consecutiveProtocolDays++;
      } else {
        break;
      }
    }

    const protocol: ProtocolInfo = {
      currentDay: currentProtocol.day,
      dayTitle: currentProtocol.title,
      daySubtitle: currentProtocol.subtitle,
      objective: currentProtocol.objective,
      estimatedMinutes: currentProtocol.estimated_minutes,
      totalCompleted,
      consecutiveDays: consecutiveProtocolDays,
    };

    // --- Weekly stats ---
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const weekAgoStr = weekAgo.toLocaleDateString('en-CA');
    
    const weeklyCheckinsCount = weekCheckins.filter((c: any) => c.checkin_date >= weekAgoStr).length;
    let bestSignal = 'Consistencia estable';
    const noBloatDays = weekCheckins.filter((c: any) => c.bloating_level === 'Ninguna').length;
    const goodEnergyDays = weekCheckins.filter((c: any) => c.energy_level === 'Buena' || c.energy_level === 'Excelente').length;
    if (noBloatDays >= 3) bestSignal = 'Mejora en hinchazón';
    else if (goodEnergyDays >= 3) bestSignal = 'Buena energía sostenida';

    const weekly: WeeklyStats = {
      checkinsCount: weeklyCheckinsCount,
      consecutiveProtocolDays,
      bestSignal,
    };

    // --- Focus items ---
    const focusItems: string[] = [];
    if (!checkinDone) {
      focusItems.push('Completa tu check-in diario');
    }
    if (waterVal < 4) {
      focusItems.push('Hidrátate: agua + desayuno con fibra');
    }
    if (energyVal === 'Baja' || energyVal === '-') {
      focusItems.push('Movimiento ligero de 10 minutos');
    }
    if (!protocolDoneToday && checkinDone) {
      focusItems.push('Completa tu paso del protocolo');
    }
    if (focusItems.length === 0) {
      focusItems.push('Mantén tu hidratación y sigue tu ritmo');
      focusItems.push('Revisa tu progreso semanal');
    }

    // --- Daily summary ---
    let dailySummary = '';
    if (checkinDone) {
      const parts: string[] = [];
      if (bloatingVal === 'Ninguna') {
        parts.push('tu cuerpo muestra una buena señal: sin hinchazón');
      } else if (bloatingVal === 'Alta' || bloatingVal === 'Moderada') {
        parts.push('tu digestión necesita apoyo hoy');
      }
      if (waterVal >= 6) {
        parts.push('tu hidratación está excelente');
      } else if (waterVal < 4 && waterVal > 0) {
        parts.push('enfócate en mantener la hidratación');
      }
      if (energyVal === 'Buena' || energyVal === 'Excelente') {
        parts.push('tu energía está en buen nivel');
      }
      if (parts.length > 0) {
        dailySummary = `Hoy ${parts.join(' y ')}. ${waterVal < 4 ? 'Prioriza agua y fibra en tu próxima comida.' : 'Sigue así.'}`;
      } else {
        dailySummary = 'Cada registro te acerca a entender mejor tu cuerpo. Sigue anotando.';
      }
    } else {
      dailySummary = 'Registra tu check-in para ver el resumen de tu día.';
    }

    // --- Insight ---
    let insightMessage = 'Para mañana, prioriza agua al despertar y desayuno con fibra. 🌱';
    if (waterVal > 4 && bloatingVal === 'Ninguna') {
      insightMessage = 'Cuando bebes más agua, tu hinchazón tiende a reducirse. ¡Excelente conexión! 💧';
    } else if (streak >= 3) {
      insightMessage = 'Tu cuerpo responde bien a la consistencia. Sigue registrando cada día. 💚';
    } else if (totalCompleted > 0 && (energyVal === 'Buena' || energyVal === 'Excelente')) {
      insightMessage = 'El protocolo está haciendo efecto: tus tareas diarias se reflejan en tu energía. ⚡';
    }

    return {
      adaptiveMessage,
      consistency,
      tasksDone,
      totalTasks,
      streak,
      comparisonText,
      checkinDone,
      protocolDoneToday,
      waterToday: waterVal,
      hydration,
      bowel,
      energy,
      bloating,
      protocol,
      weekly,
      focusItems,
      dailySummary,
      insightMessage,
    };
  }, [todayCheckin, yesterdayCheckin, weekCheckins, protocolCompletions]);

  return {
    loading,
    userName,
    avatarUrl,
    mainGoal,
    motivationLevel,
    foodPreferences,
    restrictions,
    ...derived,
  };
};
